<template src="./RiskPeriodSummary.html"></template>

<script>
import { formatPct, formatYearsOf, joinPeakLabels, rampAccent } from "@/modules/DataAnalysis/Risk/riskFormat";
import { thresholdDisplay } from "@/store/modules/risk/riskPresentation";
import RiskBadge from "@/components/DataAnalysis/Risk/RiskBadge/RiskBadge.vue";

export default {
  name: "RiskPeriodSummary",
  components: { RiskBadge },
  props: {
    summary: { type: Object, default: null },
    presentation: { type: Object, default: null },
  },
  computed: {
    // Same accent the chart legend swatch uses (riskFormat.js#rampAccent) — the
    // headline number reads as the indicator's own color, not a fixed blue.
    accent() {
      return rampAccent(this.presentation && this.presentation.id);
    },
    pctText() {
      if (!this.summary) return "—";
      return `${this.summary.prefix}${this.summary.pctDisplay == null ? "—" : this.summary.pctDisplay}%`;
    },
    yearsOfText() {
      if (!this.summary) return "";
      return formatYearsOf(this.summary.yearsWithEvent, this.summary.yearsTotal);
    },
    startLabel() {
      return this.summary ? this.formatDate(this.summary.startDate) : "";
    },
    endLabel() {
      return this.summary ? this.formatDate(this.summary.endDate) : "";
    },
    windowLabel() {
      const cw = this.summary && this.summary.climatologyWindow;
      return cw ? `${cw.start_year}–${cw.end_year}` : "";
    },
    thresholdText() {
      if (!this.summary) return "";
      return thresholdDisplay(this.summary.threshold, this.presentation);
    },
    eventPhrase() {
      return (this.summary && this.summary.eventPhrase) || "";
    },
    peak() {
      if (!this.summary || !this.summary.peakWeeks || this.summary.peakWeeks.length === 0) return null;
      const isTie = this.summary.peakWeeks.length > 1;
      return {
        prefix: isTie ? "Peak risk weeks" : "Peak risk",
        label: joinPeakLabels(this.summary.peakWeeks),
        pct: formatPct(this.summary.peakWeeks[0].pctDisplay, { active: this.summary.prefix === "~" }),
      };
    },
    // Named by the indicator (getters.js#periodSummary); chill_hours' own wording is
    // the default, so a caller that passes neither still reads correctly.
    totalsLabel() {
      return (this.summary && this.summary.totalsLabel) || "Mean season total";
    },
    recordLabel() {
      return (this.summary && this.summary.recordLabel) || "Most";
    },
    // Only set for a period-only indicator (chill_hours, weekly: false) — see
    // getters.js#periodSummary. Sits where `peak` sits for weekly indicators: the two
    // never coexist, since a period-only indicator has no weekly buckets to peak over.
    chillTotals() {
      if (!this.summary || this.summary.meanTotal == null) return null;
      const mean = thresholdDisplay(this.summary.meanTotal, this.presentation);
      const record = this.summary.recordTotal
        ? `${thresholdDisplay(this.summary.recordTotal.value, this.presentation)} (${this.summary.recordTotal.year})`
        : null;
      return { mean, record };
    },
  },
  methods: {
    formatDate(iso) {
      if (!iso) return "";
      const [y, m, d] = iso.split("-").map(Number);
      return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    },
  },
};
</script>

<style lang="scss" src="./RiskPeriodSummary.scss"></style>
